import { useMemo, useState, useCallback } from 'react';
import { STORAGE_KEY } from './constants';
import { MOCK } from './mockData';
import { loadUndoneMockIds, undoHistoryEntry } from './storage';
import type { HistoryEntry, UndoOutcome } from './types';

export type { HistoryJob, UndoPayload, HistoryEntry, UndoOutcome } from './types';
export { addCleanupHistoryEntry, removeCleanupHistoryEntry, undoHistoryEntry } from './storage';

interface Params {
  dateFilter: string;
  jobFilter: string;
  approvedByFilter: string;
}

export function useCleanupHistory({ dateFilter, jobFilter, approvedByFilter }: Params) {
  const [refreshKey, setRefreshKey] = useState(0);

  const result = useMemo(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(search);
    const emptyFlag = params.get('empty') === '1';
    const undoneMockIds = loadUndoneMockIds();
    const liveMock = MOCK.filter((e) => !undoneMockIds.has(e.id));

    let entries: HistoryEntry[] = liveMock;

    if (emptyFlag) {
      entries = [];
    } else {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const stored = JSON.parse(raw) as HistoryEntry[];
          entries = [...stored, ...liveMock];
        }
      } catch {}
    }

    const hasAnyHistory = entries.length > 0;

    const now = Date.now();
    const days = dateFilter === '30' ? 30 : dateFilter === '60' ? 60 : 90;
    const cutoff = now - days * 24 * 60 * 60 * 1000;

    const filtered = entries.filter((e) => {
      if (dateFilter !== 'custom' && new Date(e.dateChanged).getTime() < cutoff) return false;
      if (jobFilter !== 'all' && e.job !== jobFilter) return false;
      if (approvedByFilter === 'vela' && !e.approvedByVela) return false;
      if (approvedByFilter !== 'anyone' && approvedByFilter !== 'vela' && e.approvedBy !== approvedByFilter) return false;
      return true;
    });

    return { data: filtered, hasAnyHistory };
  }, [dateFilter, jobFilter, approvedByFilter, refreshKey]);

  const undo = useCallback((id: string): UndoOutcome => {
    const outcome = undoHistoryEntry(id);
    if (outcome.success) {
      setRefreshKey((k) => k + 1);
    }
    return outcome;
  }, []);

  return {
    data: result.data,
    hasAnyHistory: result.hasAnyHistory,
    loading: false,
    error: null as string | null,
    undo,
  };
}